import { useEffect, useState, useCallback } from 'react'
import { useEventStream } from '@carrent/core'
import { DataTable } from '../../components/DataTable'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Icon, PageHeader, StatusBadge } from '@carrent/ui'
import { api } from '@carrent/api'

const TABS = [
  { value: '',                   label: 'Все' },
  { value: 'active',             label: 'Активные' },
  { value: 'finished_by_user',   label: 'Ждут подтв.' },
  { value: 'confirmed_by_admin', label: 'Закрытые' },
]

/**
 * Индикатор аномалий в строке списка.
 *
 * Диспетчер держит этот экран открытым и по нему решает, за что
 * взяться. Флаг здесь отвечает на один вопрос — «эту поездку надо
 * смотреть внимательнее» — и ведёт прямо в сравнение, где написано,
 * что именно сработало. Перечислять правила в строке бессмысленно:
 * строка не место для разбора.
 */
function FlagMark({ trip }) {
  if (!trip.flag_level) return null

  const count = trip.flags?.length || 0
  const title = trip.flags?.map((f) => f.title).join(' · ') || 'Есть замечания'

  return (
    <span className={`flag-mark flag-mark--${trip.flag_level}`} title={title}>
      <Icon name="warning" size={13} />
      {count > 1 ? count : null}
    </span>
  )
}

/* Колонки принимают quickConfirm параметром: подтверждение живёт
   в состоянии страницы, а колонки должны оставаться без замыканий
   на конкретный экземпляр. */
const tripColumns = (quickConfirm) => [
  {
    key: 'car', label: 'Автомобиль', sortable: true,
    sortValue: t => (t.car ? `${t.car.brand} ${t.car.model}` : ''),
    render: t => (
      <>
        <span className="table__title">
          {t.car ? `${t.car.brand} ${t.car.model}` : `Машина #${t.car_id}`}
        </span>
        {t.car?.plate && <span className="table-plate trip-cell__plate">{t.car.plate}</span>}
        <FlagMark trip={t} />
        {t.flag_level && (
          <p className={`flag-note flag-note--${t.flag_level}`}>
            <Icon name="warning" size={14} />
            {t.flags.map((f) => f.title).join(' · ')}
          </p>
        )}
      </>
    ),
  },
  {
    key: 'driver', label: 'Водитель', cellClassName: 'table__muted', sortable: true,
    sortValue: t => t.user?.full_name || '',
    render: t => t.user?.full_name || `Водитель #${t.user_id}`,
  },
  {
    key: 'status', label: 'Статус', width: 'status',
    render: t => <StatusBadge status={t.status} />,
  },
  {
    key: 'started_at', label: 'Начало', sortable: true, cellClassName: 'table__muted',
    render: t => new Date(t.started_at).toLocaleString('ru', { dateStyle: 'short', timeStyle: 'short' }),
  },
  {
    key: 'finished_at', label: 'Конец', sortable: true, cellClassName: 'table__muted',
    render: t => t.finished_at
      ? new Date(t.finished_at).toLocaleString('ru', { dateStyle: 'short', timeStyle: 'short' })
      : '—',
  },
  {
    key: 'actions', label: '', width: 'actions',
    render: t => t.status === 'finished_by_user' && (
      <button className="btn btn--primary btn--sm" onClick={e => quickConfirm(t.id, e)}>
        Подтвердить
      </button>
    ),
  },
]

export default function AdminTripsPage() {
  const navigate = useNavigate()
  const [trips, setTrips]   = useState([])
  const [counts, setCounts] = useState({})
  const [searchParams] = useSearchParams()
  // KPI-гаджеты «Активные рейсы» и «Ждут подтверждения» приходят сюда
  // с параметром: точка входа обязана открывать уже нужный срез.
  const [filter, setFilter] = useState(
    () => searchParams.get('status') ?? 'finished_by_user',
  )
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    setLoading(true)
    api.get('/admin/trips', { params: { status: filter || undefined, per_page: 50 } })
      .then(r => {
        setTrips(r.data?.data?.items || [])
        setCounts(r.data?.data?.counts || {})
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [filter])

  useEffect(load, [load])

  /**
   * Живой список.
   *
   * Диспетчер смотрит на этот экран непрерывно: поездка, начатая
   * водителем минуту назад, должна появиться сама, без обновления
   * страницы. Событие не несёт данные — только знак, что список
   * изменился.
   */
  const { isLive } = useEventStream(
    (event) => {
      if (event.type?.startsWith('trip.') || event.type === 'car.status_changed') {
        load()
      }
    },
    { enabled: true }
  )

  const quickConfirm = async (id, e) => {
    e.stopPropagation()
    await api.post(`/trips/${id}/confirm`)
    load()
  }

  return (
    <div>
      <PageHeader
        title="Поездки"
        right={
          // Диспетчер должен отличать «ничего не происходит»
          // от «связь пропала, и я вижу устаревшее».
          <span className={`live-badge ${isLive ? 'live-badge--on' : ''}`}>
            {isLive ? 'обновляется' : 'нет связи'}
          </span>
        }
      />

      {/* Табы */}
      <div style={{ padding: '0 var(--gap) var(--gap-sm)' }}>
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }}>
          {TABS.map(t => {
            const cnt = counts[t.value]
            return (
              <button key={t.value}
                className={`btn ${filter === t.value ? 'btn--primary' : 'btn--outline'}`}
                style={{ flexShrink: 0, padding: '6px 12px', fontSize: '0.8rem',
                  display: 'flex', alignItems: 'center', gap: 4 }}
                onClick={() => setFilter(t.value)}>
                {t.label}
                {cnt > 0 && (
                  <span style={{
                    background: filter === t.value ? 'rgba(255,255,255,0.25)' : 'var(--border)',
                    color: filter === t.value ? '#fff' : 'var(--text-muted)',
                    fontSize: '0.72rem', padding: '1px 6px', borderRadius: 99, fontWeight: 700,
                  }}>{cnt}</span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      <div className="p-page flex-col" style={{ gap: 'var(--gap-sm)', paddingTop: 0 }}>
        <DataTable
          columns={tripColumns(quickConfirm)}
          rows={trips}
          loading={loading}
          onRowClick={t => navigate(`/admin/trips/${t.id}`)}
          empty={{ icon: 'trips', title: 'Нет поездок',
            text: 'В этом срезе поездок не найдено.' }}
        />
      </div>
    </div>
  )
}
